"use client";

import { useState } from "react";
import { submitFeedback } from "@/lib/api/feedback";
import type { FeedbackRequest } from "@/lib/api/feedback";
import { canShowPrompt, recordPromptShown } from "@/lib/feedback/promptCeiling";
import type { RatingChip } from "@/lib/feedback/ratingTypes";
import { WebStarsRow, WebNpsRow, WebChipRow, WebThumbBtn } from "./RatingPrompt.rows";

/**
 * Reusable feedback widget for web — mirrors the mobile RatingPrompt API.
 *
 * Modes:
 *   "thumbs"         — thumbs-up / thumbs-down (inline SVG icons, no emojis)
 *   "stars"          — 1–5 star picker
 *   "thumbs+text"    — thumbs + optional free-text
 *   "thumbs+reasons" — thumbs; a thumbs-DOWN reveals fixed reason chips (pass
 *                      `reasons`). Up submits immediately; a reason tap submits
 *                      negative with the reason in context.reasons[] + as text
 *                      (so tag_feedback_themes.py can theme-code it). Higher
 *                      completion than free-text, and structured/measurable.
 *   "nps"            — 0–10 horizontal recommend scale (true Net Promoter Score).
 *                      Tapping a number submits rating=<0-10> immediately. Pair
 *                      with source="in_app_nps".
 *   "intent-chips"   — chip-select (pass `chips` prop)
 *
 * Dedup: when `dedupeKey` is set, localStorage prevents re-submission on
 * the same browser session.
 *
 * Type-safety note: `source` is cast because generated api-client types
 * don't expose the native `vedic_feedback_source` enum. PostgreSQL is the
 * authoritative validation gate.
 *
 * Prompt ceiling: `canShowPrompt(source)` is checked on mount via a
 * useEffect gate. If the session's modal ceiling is already consumed or a
 * cooldown is active, the component renders nothing. After successful
 * submission, `recordPromptShown(source)` updates localStorage/
 * sessionStorage so future loads respect the ceiling.
 */

export type { RatingChip };

export interface RatingPromptProps {
  source: string;
  context?: Record<string, unknown>;
  mode:
    | "thumbs"
    | "stars"
    | "thumbs+text"
    | "stars+text"
    | "thumbs+reasons"
    | "nps"
    | "intent-chips";
  prompt?: string;
  chips?: RatingChip[];
  /** Reason chips revealed on a thumbs-down in "thumbs+reasons" mode. */
  reasons?: RatingChip[];
  dedupeKey?: string;
  onSubmit?: () => void;
}

const DEDUPE_NS = "feedback_submitted";

function isAlreadySubmitted(key: string): boolean {
  try {
    return localStorage.getItem(`${DEDUPE_NS}:${key}`) === "true";
  } catch {
    return false;
  }
}

function markSubmitted(key: string): void {
  try {
    localStorage.setItem(`${DEDUPE_NS}:${key}`, "true");
  } catch {
    // Ignore localStorage errors (private browsing, etc.)
  }
}

function feedbackSource(source: string): FeedbackRequest["source"] {
  return source as FeedbackRequest["source"];
}

export default function RatingPrompt({
  source,
  context,
  mode,
  prompt,
  chips,
  reasons,
  dedupeKey,
  onSubmit,
}: RatingPromptProps) {
  const [thumbs, setThumbs] = useState<"up" | "down" | null>(null);
  const [stars, setStars] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [chipId, setChipId] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [alreadyDone] = useState(() => (dedupeKey ? isAlreadySubmitted(dedupeKey) : false));
  const [ceilingBlocked] = useState(() => !canShowPrompt(source));

  async function doSubmit(payload: {
    rating?: number;
    text?: string;
    extraContext?: Record<string, unknown>;
  }) {
    try {
      await submitFeedback({
        source: feedbackSource(source),
        rating: payload.rating,
        text: payload.text,
        context: { ...context, ...payload.extraContext },
      });
      if (dedupeKey) markSubmitted(dedupeKey);
      // Record against the ceiling after successful submission.
      recordPromptShown(source);
      setSubmitted(true);
      onSubmit?.();
    } catch {
      // Soft-fail — feedback errors must never disrupt the chat UX
    }
  }

  if (ceilingBlocked || submitted || alreadyDone) {
    if (ceilingBlocked) return null;
    return <span style={{ fontSize: 11, color: "var(--ish-text-faint)" }}>Thank you</span>;
  }

  function handleThumb(value: "up" | "down") {
    setThumbs(value);
    if (mode === "thumbs") {
      void doSubmit({ rating: value === "up" ? 1 : 0 });
    }
    // thumbs+reasons: an up is positive and submits now; a down reveals the
    // reason chips (rendered below) and waits for the reason tap.
    if (mode === "thumbs+reasons" && value === "up") {
      void doSubmit({ rating: 1 });
    }
  }

  function handleReason(id: string) {
    const label = reasons?.find((r) => r.id === id)?.label ?? id;
    void doSubmit({ rating: 0, text: label, extraContext: { reasons: [id] } });
  }

  function handleStars(n: number) {
    setStars(n);
    if (mode === "stars") {
      void doSubmit({ rating: n });
    }
    // In "stars+text" mode, wait for the Submit button press.
  }

  // NPS 0–10: submit the chosen score immediately (true Net Promoter Score).
  function handleNps(score: number) {
    void doSubmit({ rating: score });
  }

  function handleSubmitWithStarsText() {
    void doSubmit({
      rating: stars || undefined,
      text: text.trim() || undefined,
    });
  }

  function handleChip(id: string) {
    setChipId(id);
    void doSubmit({ text: id, extraContext: { chip_id: id } });
  }

  function handleSubmitWithText() {
    void doSubmit({
      rating: thumbs === "up" ? 1 : 0,
      text: text.trim() || undefined,
    });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {prompt && (
        <p style={{ margin: 0, fontSize: 11, color: "var(--ish-text-muted)" }}>{prompt}</p>
      )}

      <WebRatingControls
        mode={mode}
        chips={chips}
        reasons={reasons}
        thumbs={thumbs}
        stars={stars}
        hoveredStar={hoveredStar}
        chipId={chipId}
        text={text}
        onTextChange={setText}
        onThumb={handleThumb}
        onTextSubmit={handleSubmitWithText}
        onStarsTextSubmit={handleSubmitWithStarsText}
        onStars={handleStars}
        onStarHover={setHoveredStar}
        onNps={handleNps}
        onChip={handleChip}
        onReason={handleReason}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internal: thumb button (SVG-based, no emoji)
// ---------------------------------------------------------------------------

interface WebRatingControlsProps {
  mode: RatingPromptProps["mode"];
  chips?: RatingChip[];
  reasons?: RatingChip[];
  thumbs: "up" | "down" | null;
  stars: number;
  hoveredStar: number;
  chipId: string | null;
  text: string;
  onTextChange: (value: string) => void;
  onThumb: (value: "up" | "down") => void;
  onTextSubmit: () => void;
  onStarsTextSubmit: () => void;
  onStars: (value: number) => void;
  onStarHover: (value: number) => void;
  onNps: (value: number) => void;
  onChip: (value: string) => void;
  onReason: (value: string) => void;
}

function WebRatingControls({
  mode,
  chips,
  reasons,
  thumbs,
  stars,
  hoveredStar,
  chipId,
  text,
  onTextChange,
  onThumb,
  onTextSubmit,
  onStarsTextSubmit,
  onStars,
  onStarHover,
  onNps,
  onChip,
  onReason,
}: WebRatingControlsProps) {
  switch (mode) {
    case "thumbs":
      return <WebThumbsRow thumbs={thumbs} onPress={onThumb} />;
    case "thumbs+text":
      return (
        <WebThumbsTextControls
          text={text}
          thumbs={thumbs}
          onTextChange={onTextChange}
          onTextSubmit={onTextSubmit}
          onThumb={onThumb}
        />
      );
    case "thumbs+reasons":
      return (
        <WebThumbsReasonControls
          chipId={chipId}
          reasons={reasons}
          thumbs={thumbs}
          onReason={onReason}
          onThumb={onThumb}
        />
      );
    case "stars":
      return (
        <WebStarsRow
          stars={stars}
          hoveredStar={hoveredStar}
          onPress={onStars}
          onHover={onStarHover}
        />
      );
    case "stars+text":
      return (
        <WebStarsTextControls
          hoveredStar={hoveredStar}
          stars={stars}
          text={text}
          onStarHover={onStarHover}
          onStars={onStars}
          onStarsTextSubmit={onStarsTextSubmit}
          onTextChange={onTextChange}
        />
      );
    case "nps":
      return <WebNpsRow onPress={onNps} />;
    case "intent-chips":
      return chips ? <WebChipRow chips={chips} activeChipId={chipId} onPress={onChip} /> : null;
  }
}

function WebThumbsTextControls({
  text,
  thumbs,
  onTextChange,
  onTextSubmit,
  onThumb,
}: {
  text: string;
  thumbs: "up" | "down" | null;
  onTextChange: (value: string) => void;
  onTextSubmit: () => void;
  onThumb: (value: "up" | "down") => void;
}) {
  return (
    <>
      <WebThumbsRow thumbs={thumbs} onPress={onThumb} />
      {thumbs !== null && (
        <WebFeedbackTextBox text={text} onTextChange={onTextChange} onSubmit={onTextSubmit} />
      )}
    </>
  );
}

function WebThumbsReasonControls({
  chipId,
  reasons,
  thumbs,
  onReason,
  onThumb,
}: {
  chipId: string | null;
  reasons?: RatingChip[];
  thumbs: "up" | "down" | null;
  onReason: (value: string) => void;
  onThumb: (value: "up" | "down") => void;
}) {
  return (
    <>
      <WebThumbsRow thumbs={thumbs} onPress={onThumb} />
      {thumbs === "down" && reasons && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
          <p style={{ margin: 0, fontSize: 11, color: "var(--ish-text-muted)" }}>What felt off?</p>
          <WebChipRow chips={reasons} activeChipId={chipId} onPress={onReason} />
        </div>
      )}
    </>
  );
}

function WebStarsTextControls({
  hoveredStar,
  stars,
  text,
  onStarHover,
  onStars,
  onStarsTextSubmit,
  onTextChange,
}: {
  hoveredStar: number;
  stars: number;
  text: string;
  onStarHover: (value: number) => void;
  onStars: (value: number) => void;
  onStarsTextSubmit: () => void;
  onTextChange: (value: string) => void;
}) {
  return (
    <>
      <WebStarsRow
        stars={stars}
        hoveredStar={hoveredStar}
        onPress={onStars}
        onHover={onStarHover}
      />
      {stars > 0 && (
        <WebFeedbackTextBox text={text} onTextChange={onTextChange} onSubmit={onStarsTextSubmit} />
      )}
    </>
  );
}

interface WebThumbsRowProps {
  thumbs: "up" | "down" | null;
  onPress: (value: "up" | "down") => void;
}

function WebThumbsRow({ thumbs, onPress }: WebThumbsRowProps) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      <WebThumbBtn kind="up" active={thumbs === "up"} onPress={() => onPress("up")} />
      <WebThumbBtn kind="down" active={thumbs === "down"} onPress={() => onPress("down")} />
    </div>
  );
}

interface WebFeedbackTextBoxProps {
  text: string;
  onTextChange: (value: string) => void;
  onSubmit: () => void;
}

function WebFeedbackTextBox({ text, onTextChange, onSubmit }: WebFeedbackTextBoxProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
      <textarea
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        placeholder="Tell us more (optional)"
        rows={2}
        style={{
          fontSize: 11,
          padding: "4px 6px",
          borderRadius: 6,
          border: "1px solid var(--ish-border)",
          background: "var(--color-bg)",
          color: "var(--color-dark)",
          resize: "none",
        }}
      />
      <button
        type="button"
        onClick={onSubmit}
        style={{
          fontSize: 11,
          padding: "4px 10px",
          borderRadius: 6,
          border: "none",
          background: "var(--surface-brand-strong)",
          color: "#fff",
          cursor: "pointer",
          alignSelf: "flex-start",
        }}
      >
        Submit
      </button>
    </div>
  );
}
